import { useQuery } from "@apollo/client";
import useCapabilities, { useCanViewTasksBool } from "@/hooks/useCapabilities";
import useEffectiveDisplayUser from "@/hooks/useEffectiveDisplayUser";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import { TaskStageValues } from "@/types";
import { isInitialQueryLoading } from "../_utils/isInitialQueryLoading";

export const UPCOMING_TASK_LIMIT = 3;

const UPCOMING_TASK_ORDER_BY = {
  field: "DUE_DATE",
  direction: "ASC"
} as const;

export function upcomingMyTasksVariables(assignedToId: string) {
  return {
    first: UPCOMING_TASK_LIMIT,
    input: {
      assignedToId,
      stage: [TaskStageValues.OPEN]
    },
    orderBy: UPCOMING_TASK_ORDER_BY
  };
}

export function upcomingTeamTasksVariables(teamTag: string) {
  return {
    first: UPCOMING_TASK_LIMIT,
    input: {
      teamTag,
      unassigned: true,
      stage: [TaskStageValues.OPEN]
    },
    orderBy: UPCOMING_TASK_ORDER_BY
  };
}

export default function useUpcomingTasks() {
  const { id: currentUserId } = useCapabilities();
  const canViewTasks = useCanViewTasksBool();
  const { department } = useEffectiveDisplayUser();
  const teamName = department?.trim() ?? "";
  const skipMyTasks = !currentUserId || !canViewTasks;
  const skipTeamTasks = !teamName || !canViewTasks;

  const {
    data: myData,
    previousData: myPreviousData,
    loading: myLoading,
    networkStatus: myNetworkStatus,
    error: myError
  } = useQuery(TASKS_QUERY, {
    variables: upcomingMyTasksVariables(currentUserId ?? ""),
    skip: skipMyTasks,
    fetchPolicy: "cache-and-network"
  });

  const {
    data: teamData,
    previousData: teamPreviousData,
    loading: teamLoading,
    networkStatus: teamNetworkStatus,
    error: teamError
  } = useQuery(TASKS_QUERY, {
    variables: upcomingTeamTasksVariables(teamName),
    skip: skipTeamTasks,
    fetchPolicy: "cache-and-network"
  });

  const resolvedMyData = myData ?? myPreviousData;
  const tasks = (resolvedMyData?.tasks?.nodes ?? []).filter(Boolean);
  const resolvedTeamData = teamData ?? teamPreviousData;
  const teamTasks = (resolvedTeamData?.tasks?.nodes ?? []).filter(Boolean);

  return {
    tasks,
    totalOpenTaskCount: resolvedMyData?.tasks?.totalCount ?? 0,
    loading: skipMyTasks
      ? false
      : isInitialQueryLoading({
          loading: myLoading,
          networkStatus: myNetworkStatus,
          itemCount: tasks.length,
          hasData: resolvedMyData !== undefined,
          error: myError
        }),
    error: myError as Error | undefined,
    teamTasks,
    totalOpenTeamTaskCount: resolvedTeamData?.tasks?.totalCount ?? 0,
    teamTasksLoading: skipTeamTasks
      ? false
      : isInitialQueryLoading({
          loading: teamLoading,
          networkStatus: teamNetworkStatus,
          itemCount: teamTasks.length,
          hasData: resolvedTeamData !== undefined,
          error: teamError
        }),
    teamTasksError: teamError as Error | undefined,
    canViewTasks
  };
}
